import { FC, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@components/layout/DashboardLayout';
import { EnrichedSimulation, PortfolioSummary } from '@/types/index';
import { portfolioService } from '@services/portfolio.service';

// ─── Formatters ──────────────────────────────────────────────────────────────

const fmtChf = (v: number | null | undefined) =>
  v == null || isNaN(v) ? '—' : `CHF ${new Intl.NumberFormat('fr-CH').format(Math.round(v))}`;
const fmtPct = (v: number | null | undefined) =>
  v == null || isNaN(v) ? '—' : `${v.toFixed(2)} %`;

// ─── Best-value logic ─────────────────────────────────────────────────────────

function bestIndex(values: (number | null)[], higherIsBetter: boolean): number | null {
  const valid = values
    .map((v, i) => ({ v, i }))
    .filter((x): x is { v: number; i: number } => x.v != null);
  if (valid.length < 2) return null;
  return (
    higherIsBetter
      ? valid.reduce((a, b) => (b.v > a.v ? b : a))
      : valid.reduce((a, b) => (b.v < a.v ? b : a))
  ).i;
}

// ─── SimulationSelectCard ─────────────────────────────────────────────────────

const SimulationSelectCard: FC<{
  sim: EnrichedSimulation;
  selected: boolean;
  onToggle: (id: string) => void;
  onDelete: (sim: EnrichedSimulation) => void;
  isDeleting: boolean;
}> = ({ sim, selected, onToggle, onDelete, isDeleting }) => {
  const r = sim.results;
  const cfPositive = r.cashFlowMonthly > 0;
  const ltvPct = r.LTV * 100;

  return (
    <div
      onClick={() => onToggle(sim.id)}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-colors ${
        selected
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'
      }`}
    >
      {/* Checkbox */}
      <div className={`w-4 h-4 rounded shrink-0 border-2 flex items-center justify-center transition-colors ${
        selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-gray-600'
      }`}>
        {selected && (
          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
            <path d="M10 3L5 8.5 2 5.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        )}
      </div>

      {/* Nom + date */}
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate block">{sim.name}</span>
        <span className="text-xs text-gray-400">{new Date(sim.createdAt).toLocaleDateString('fr-CH')}</span>
      </div>

      {/* KPIs inline */}
      <div className="flex items-center gap-3 shrink-0 text-xs">
        <span className={`font-medium ${cfPositive ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
          CF {fmtChf(r.cashFlowMonthly)}/mois
        </span>
        <span className={`font-medium ${ltvPct > 80 ? 'text-red-500' : ltvPct > 65 ? 'text-amber-500' : 'text-green-600 dark:text-green-400'}`}>
          LTV {ltvPct.toFixed(1)} %
        </span>
        <span className="text-gray-600 dark:text-gray-400">
          Rdt {fmtPct(r.netYield)}
        </span>
      </div>

      {/* Bouton supprimer */}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(sim); }}
        disabled={isDeleting}
        className="p-1 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 transition-colors shrink-0 disabled:opacity-40"
        title="Supprimer ce scénario"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>
  );
};

// ─── ComparisonTable ──────────────────────────────────────────────────────────

const ComparisonTable: FC<{
  compared: EnrichedSimulation[];
  portfolioSummary: PortfolioSummary | null;
}> = ({ compared, portfolioSummary }) => {
  const n = compared.length;

  // Cell class depending on whether this column index is the "best"
  const cellCls = (isBest: boolean) =>
    isBest
      ? 'px-3 py-2 text-right text-xs bg-green-50 dark:bg-green-900/20 font-bold text-green-700 dark:text-green-300'
      : 'px-3 py-2 text-right text-xs text-gray-700 dark:text-gray-300';

  // Pre-compute best indices for each metric
  const bestFonds = bestIndex(compared.map(s => s.results.equityRequired), false);
  const bestPret = bestIndex(compared.map(s => s.results.loanAmount), false);
  const bestLTV = bestIndex(compared.map(s => s.results.LTV), false);
  const bestNOI = bestIndex(compared.map(s => s.results.NOI), true);
  const bestCF = bestIndex(compared.map(s => s.results.cashFlowMonthly), true);
  const bestCoC = bestIndex(compared.map(s => s.results.cashOnCash), true);
  const bestRdt = bestIndex(compared.map(s => s.results.netYield), true);
  const bestCap = bestIndex(compared.map(s => s.results.capRate), true);
  const bestInt = bestIndex(compared.map(s => s.results.annualInterest), false);

  // Impact computations
  const impacts = portfolioSummary
    ? compared.map(s => ({
        cfAfter: (portfolioSummary.totalCashFlow + s.results.cashFlow) / 12,
        ltvAfter:
          portfolioSummary.totalMarketValue + s.parameters.purchasePrice > 0
            ? (portfolioSummary.totalDebt + s.results.loanAmount) /
              (portfolioSummary.totalMarketValue + s.parameters.purchasePrice)
            : null,
        noiAfter: portfolioSummary.totalNOI + s.results.NOI,
      }))
    : null;

  const bestImpactCF = impacts ? bestIndex(impacts.map(i => i.cfAfter), true) : null;
  const bestImpactLTV = impacts ? bestIndex(impacts.map(i => i.ltvAfter), false) : null;
  const bestImpactNOI = impacts ? bestIndex(impacts.map(i => i.noiAfter), true) : null;

  const thCls = 'px-3 py-2 text-right text-xs font-semibold text-white bg-blue-600 dark:bg-blue-700';
  const labelCls = 'px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 whitespace-nowrap';
  const sectionCls = 'px-3 py-2 text-left text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider bg-gray-100 dark:bg-gray-800';

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table className="w-full text-sm border-collapse min-w-[600px]">
        <thead>
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold text-white bg-blue-600 dark:bg-blue-700 w-44">
              Métrique
            </th>
            {compared.map((sim) => (
              <th key={sim.id} className={thCls}>
                <div className="text-white font-semibold truncate max-w-[160px]">{sim.name}</div>
                <div className="text-blue-200 font-normal text-[10px] truncate max-w-[160px]">
                  {sim.property
                    ? `${sim.property.address ?? ''}, ${sim.property.city}`
                    : '—'}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* ── Section Paramètres ── */}
          <tr><td colSpan={n + 1} className={sectionCls}>Paramètres</td></tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>Prix d'achat</td>
            {compared.map(s => (
              <td key={s.id} className="px-3 py-2 text-right text-xs text-gray-700 dark:text-gray-300">
                {fmtChf(s.parameters.purchasePrice)}
              </td>
            ))}
          </tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>Apport propre</td>
            {compared.map(s => (
              <td key={s.id} className="px-3 py-2 text-right text-xs text-gray-700 dark:text-gray-300">
                {s.parameters.downPaymentPct} %
              </td>
            ))}
          </tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>Taux d'intérêt</td>
            {compared.map(s => (
              <td key={s.id} className="px-3 py-2 text-right text-xs text-gray-700 dark:text-gray-300">
                {s.parameters.interestRate} %
              </td>
            ))}
          </tr>

          {/* ── Section Financement ── */}
          <tr><td colSpan={n + 1} className={sectionCls}>Financement</td></tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>Fonds propres requis</td>
            {compared.map((s, i) => (
              <td key={s.id} className={cellCls(i === bestFonds)}>
                {fmtChf(s.results.equityRequired)}
                {i === bestFonds && <span className="ml-1">✓</span>}
              </td>
            ))}
          </tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>Montant du prêt</td>
            {compared.map((s, i) => (
              <td key={s.id} className={cellCls(i === bestPret)}>
                {fmtChf(s.results.loanAmount)}
                {i === bestPret && <span className="ml-1">✓</span>}
              </td>
            ))}
          </tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>LTV</td>
            {compared.map((s, i) => {
              const pct = s.results.LTV * 100;
              const colorCls = i === bestLTV
                ? ''
                : pct > 80 ? 'text-red-500 dark:text-red-400' : pct > 65 ? 'text-amber-500 dark:text-amber-400' : '';
              return (
                <td key={s.id} className={`${cellCls(i === bestLTV)} ${colorCls}`}>
                  {pct.toFixed(1)} %{i === bestLTV && <span className="ml-1">✓</span>}
                </td>
              );
            })}
          </tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>Intérêts annuels</td>
            {compared.map((s, i) => (
              <td key={s.id} className={cellCls(i === bestInt)}>
                {fmtChf(s.results.annualInterest)}
                {i === bestInt && <span className="ml-1">✓</span>}
              </td>
            ))}
          </tr>

          {/* ── Section Rendement ── */}
          <tr><td colSpan={n + 1} className={sectionCls}>Rendement</td></tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>NOI annuel</td>
            {compared.map((s, i) => (
              <td key={s.id} className={cellCls(i === bestNOI)}>
                {fmtChf(s.results.NOI)}
                {i === bestNOI && <span className="ml-1">✓</span>}
              </td>
            ))}
          </tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>Cash-flow / mois</td>
            {compared.map((s, i) => (
              <td key={s.id} className={`${cellCls(i === bestCF)} ${s.results.cashFlowMonthly < 0 && i !== bestCF ? 'text-red-500 dark:text-red-400' : ''}`}>
                {fmtChf(s.results.cashFlowMonthly)}
                {i === bestCF && <span className="ml-1">✓</span>}
              </td>
            ))}
          </tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>Rendement net</td>
            {compared.map((s, i) => (
              <td key={s.id} className={cellCls(i === bestRdt)}>
                {fmtPct(s.results.netYield)}
                {i === bestRdt && <span className="ml-1">✓</span>}
              </td>
            ))}
          </tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>Cash-on-cash</td>
            {compared.map((s, i) => (
              <td key={s.id} className={cellCls(i === bestCoC)}>
                {fmtPct(s.results.cashOnCash)}
                {i === bestCoC && <span className="ml-1">✓</span>}
              </td>
            ))}
          </tr>
          <tr className="border-t border-gray-100 dark:border-gray-700">
            <td className={labelCls}>Cap rate</td>
            {compared.map((s, i) => (
              <td key={s.id} className={cellCls(i === bestCap)}>
                {fmtPct(s.results.capRate)}
                {i === bestCap && <span className="ml-1">✓</span>}
              </td>
            ))}
          </tr>

          {/* ── Section Impact portefeuille ── */}
          {impacts && (
            <>
              <tr><td colSpan={n + 1} className={sectionCls}>Impact portefeuille si acquisition</td></tr>
              <tr className="border-t border-gray-100 dark:border-gray-700">
                <td className={labelCls}>CF mensuel global (après)</td>
                {impacts.map((imp, i) => (
                  <td key={compared[i].id} className={cellCls(i === bestImpactCF)}>
                    {fmtChf(imp.cfAfter)}
                    {i === bestImpactCF && <span className="ml-1">✓</span>}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-gray-100 dark:border-gray-700">
                <td className={labelCls}>LTV globale (après)</td>
                {impacts.map((imp, i) => (
                  <td key={compared[i].id} className={cellCls(i === bestImpactLTV)}>
                    {imp.ltvAfter != null ? `${(imp.ltvAfter * 100).toFixed(1)} %` : '—'}
                    {i === bestImpactLTV && <span className="ml-1">✓</span>}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-gray-100 dark:border-gray-700">
                <td className={labelCls}>NOI global (après)</td>
                {impacts.map((imp, i) => (
                  <td key={compared[i].id} className={cellCls(i === bestImpactNOI)}>
                    {fmtChf(imp.noiAfter)}
                    {i === bestImpactNOI && <span className="ml-1">✓</span>}
                  </td>
                ))}
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const Comparateur: FC = () => {
  const navigate = useNavigate();
  const [allSims, setAllSims] = useState<EnrichedSimulation[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [portfolioSummary, setPortfolioSummary] = useState<PortfolioSummary | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      portfolioService.getAllSimulations(),
      portfolioService.getSummary(),
    ])
      .then(([sims, summary]) => {
        setAllSims(sims);
        setPortfolioSummary(summary);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, { key: string; label: string; askingPrice: number | null; sims: EnrichedSimulation[] }>();
    for (const sim of allSims) {
      const key = sim.propertyId ?? sim.portfolioPropertyId ?? 'standalone';
      if (!map.has(key)) {
        const label = sim.property
          ? `${sim.property.address ? sim.property.address + ', ' : ''}${sim.property.city}`
          : 'Sans bien catalogue associé';
        map.set(key, { key, label, askingPrice: sim.property?.askingPrice ?? null, sims: [] });
      }
      map.get(key)!.sims.push(sim);
    }
    return Array.from(map.values());
  }, [allSims]);

  const compared = useMemo(
    () => allSims.filter(s => selectedIds.has(s.id)),
    [allSims, selectedIds],
  );

  const toggle = (id: string) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleDelete = async (sim: EnrichedSimulation) => {
    if (!confirm(`Supprimer le scénario "${sim.name}" ?`)) return;
    setDeletingId(sim.id);
    try {
      if (sim.propertyId) {
        await portfolioService.deleteCatalogSimulation(sim.propertyId, sim.id);
      } else if (sim.portfolioPropertyId) {
        await portfolioService.deleteSimulation(sim.portfolioPropertyId, sim.id);
      }
      setAllSims(prev => prev.filter(s => s.id !== sim.id));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(sim.id); return next; });
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <DashboardLayout title="Comparateur de scénarios">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Comparateur de scénarios
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {allSims.length} scénario{allSims.length !== 1 ? 's' : ''} au total
              {selectedIds.size > 0 && ` — ${selectedIds.size} sélectionné${selectedIds.size !== 1 ? 's' : ''}`}
            </p>
          </div>
          {selectedIds.size > 0 && (
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
            >
              Tout désélectionner
            </button>
          )}
        </div>

        {/* Panel liste */}
        {isLoading ? (
          <div className="flex items-center gap-2 py-12 text-gray-400">
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            Chargement des scénarios…
          </div>
        ) : allSims.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
            <span className="text-4xl">⚖️</span>
            <p className="text-sm font-medium">Aucun scénario enregistré</p>
            <p className="text-xs">Créez des scénarios depuis la page Analyse d'une opportunité.</p>
            <button
              onClick={() => navigate('/opportunities')}
              className="mt-2 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
            >
              Voir les opportunités
            </button>
          </div>
        ) : (
          <div className="space-y-4 max-h-[45vh] overflow-y-auto pr-1">
            {grouped.map(group => (
              <div key={group.key} className="space-y-1.5">
                <div className="flex items-baseline gap-2 px-1">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{group.label}</h3>
                  {group.askingPrice && (
                    <span className="text-xs text-gray-400">— {fmtChf(group.askingPrice)}</span>
                  )}
                  {group.sims[0]?.propertyId && (
                    <button
                      onClick={() => navigate(`/analysis/${group.sims[0].propertyId}`)}
                      className="text-xs text-blue-500 hover:underline ml-auto"
                    >
                      Voir l'analyse →
                    </button>
                  )}
                </div>
                {group.sims.map(sim => (
                  <SimulationSelectCard
                    key={sim.id}
                    sim={sim}
                    selected={selectedIds.has(sim.id)}
                    onToggle={toggle}
                    onDelete={handleDelete}
                    isDeleting={deletingId === sim.id}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Panel comparaison */}
        {!isLoading && allSims.length > 0 && (
          compared.length >= 2 ? (
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                Comparaison — {compared.length} scénarios
              </h2>
              <ComparisonTable compared={compared} portfolioSummary={portfolioSummary} />
            </div>
          ) : (
            <div className="flex items-center justify-center py-8 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 text-gray-400 gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-sm">
                Sélectionnez au moins 2 scénarios ci-dessus pour les comparer
              </p>
            </div>
          )
        )}
      </div>
    </DashboardLayout>
  );
};
